import { motion } from "framer-motion";
import { ArrowRight, Building2, HelpCircle } from "lucide-react";

const ASSETS = {
  logoWhite: "https://customer-assets.emergentagent.com/job_venue-revenue/artifacts/rcizesv6_AnytimeFumeWhiteLOGO.png",
  machine1: "https://customer-assets.emergentagent.com/job_venue-revenue/artifacts/fvikj88q_Untitled%20design-17.png",
  machine2: "https://customer-assets.emergentagent.com/job_venue-revenue/artifacts/dh05gbdu_Untitled%20design-18.png"
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 }
  }
};

const HomePage = () => {
  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Header */}
      <header className="py-6 px-6 md:px-12 !opacity-[50%]">
        <div className="max-w-7xl mx-auto flex justify-center">
          <div className="flex items-center gap-3">
            <img src={ASSETS.logoWhite} alt="Anytime Fume" className="h-10 md:h-12 w-auto !rounded-t-none !rounded-b-none !rounded-l-none !rounded-r-none" />
            <span className="font-body text-xl md:text-2xl tracking-[0.2em] !opacity-[50%] text-white">ANYTIME FUME</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12 md:py-0">
        <div className="max-w-6xl mx-auto w-full">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center mb-16">

            <motion.p variants={fadeInUp} className="text-white/50 text-xs uppercase tracking-[0.3em] mb-4 font-body">
              Perfume Vending Machines
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-6">
              Smell Luxury <span className="italic text-white/80">On The Go</span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="font-body text-white/60 text-base md:text-lg max-w-2xl mx-auto">
              Australia's first designer fragrance vending experience. Single sprays of premium perfumes, dispensed instantly.
            </motion.p>
          </motion.div>

          {/* Machine Images */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="flex justify-center items-end gap-4 md:gap-8 mb-16">

            <img
              src={ASSETS.machine1}
              alt="Anytime Fume Machine"
              className="h-[180px] md:h-[250px] lg:h-[300px] w-auto object-contain" />

            <img
              src={ASSETS.machine2}
              alt="Anytime Fume Machine"
              className="h-[180px] md:h-[250px] lg:h-[300px] w-auto object-contain" />

          </motion.div>

          {/* Two Options */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">

            {/* Partnership Enquiries */}
            <motion.a
              href="/partner"
              variants={fadeInUp}
              className="group relative p-8 md:p-10 border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20 transition-all duration-500"
              data-testid="partnership-card">

              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 border border-white/20 flex items-center justify-center text-white/60 group-hover:text-white group-hover:border-white/40 transition-all">
                  <Building2 className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h2 className="font-heading text-2xl md:text-3xl text-white mb-2">Partnership Enquiries</h2>
                  <p className="font-body text-white/50 text-sm">For venue owners & business partners</p>
                </div>
              </div>
              <p className="font-body text-white/60 text-sm leading-relaxed mb-6">
                Interested in hosting an Anytime Fume machine at your venue? Register your interest and we'll be in touch.
              </p>
              <div className="flex items-center gap-2 text-white/70 group-hover:text-white transition-colors font-body text-sm">
                <span>Learn More</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.a>

            {/* Customer Support */}
            <motion.a
              href="/support"
              variants={fadeInUp}
              className="group relative p-8 md:p-10 border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20 transition-all duration-500"
              data-testid="support-card">

              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 border border-white/20 flex items-center justify-center text-white/60 group-hover:text-white group-hover:border-white/40 transition-all">
                  <HelpCircle className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h2 className="font-heading text-2xl md:text-3xl text-white mb-2">Customer Support</h2>
                  <p className="font-body text-white/50 text-sm">Need help with your purchase?</p>
                </div>
              </div>
              <p className="font-body text-white/60 text-sm leading-relaxed mb-6">
                Had an issue with a spray or need assistance? We're here to help. Submit a request and we'll respond promptly.
              </p>
              <div className="flex items-center gap-2 text-white/70 group-hover:text-white transition-colors font-body text-sm">
                <span>Get Support</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.a>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto text-center">
          <p className="font-body text-white/40 text-xs mb-1">
            Anytime Fume Pty Ltd
          </p>
          <p className="font-body text-white/30 text-[10px]">
            ABN: 25 693 934 217
          </p>
        </div>
      </footer>
    </div>);

};

export default HomePage;